import React, { useEffect, useState } from "react";
import "./NewPagination.css";
import Arrow from "../../../../assets/images/rightAnglePath.svg";

const Pagination = ({
  totalPages = 1,
  currentPage = 1,
  onPageChange = () => {},
}) => {
  const maxVisiblePages = 5;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePrevClick = () => {
    if (currentPage > 1) onPageChange(currentPage - 1);
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) onPageChange(currentPage + 1);
  };

  const handlePageClick = (page) => {
    if (page !== "...") onPageChange(page);
  };

  // Mobile: always show a sliding window of up to 3 page numbers, so users
  // can tell how many pages exist even when sitting on the last page.
  const mobileWindowSize = 3;
  const getMobileVisiblePages = () => {
    if (totalPages <= mobileWindowSize) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    let start = Math.max(1, currentPage - 1);
    let end = start + mobileWindowSize - 1;
    if (end > totalPages) {
      end = totalPages;
      start = end - mobileWindowSize + 1;
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  // Old desktop logic — had a near-end branch that widened the window to 4
  // pages (e.g. "1 ... 6 7 8 9"), making the ellipsis feel lopsided.
  // const getVisiblePages = () => {
  //   if (totalPages <= maxVisiblePages) {
  //     return Array.from({ length: totalPages }, (_, i) => i + 1);
  //   }
  //   const pages = [];
  //   pages.push(1);
  //   let start = Math.max(2, currentPage - 1);
  //   let end = Math.min(totalPages - 1, currentPage + 1);
  //   if (currentPage <= 3) { start = 2; end = 4; }
  //   if (currentPage >= totalPages - 2) { start = totalPages - 3; end = totalPages - 1; }
  //   if (start > 2) { if (start === 3) { pages.push(2); } else { pages.push("..."); } }
  //   for (let i = start; i <= end; i++) { if (i > 1 && i < totalPages) pages.push(i); }
  //   if (end < totalPages - 1) { if (end === totalPages - 2) { pages.push(totalPages - 1); } else { pages.push("..."); } }
  //   if (totalPages > 1) pages.push(totalPages);
  //   return pages;
  // };

  // Desktop: strictly show exactly maxVisiblePages page numbers at all times.
  // Page 1 and totalPages are permanent anchors (2 slots); the remaining
  // (maxVisiblePages - 2) slots slide as a window centered on currentPage.
  // "..." marks any gap — it is NOT counted toward the page-number limit.
  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const flexCount = maxVisiblePages - 2; // middle slots after anchoring 1 and last
    let flexStart = Math.max(2, currentPage - Math.floor(flexCount / 2));
    let flexEnd   = flexStart + flexCount - 1;

    // Shift window left if it runs past the second-to-last page
    if (flexEnd > totalPages - 1) {
      flexEnd   = totalPages - 1;
      flexStart = Math.max(2, flexEnd - flexCount + 1);
    }

    const pages = [1];
    if (flexStart > 2)              pages.push("...");
    for (let i = flexStart; i <= flexEnd; i++) pages.push(i);
    if (flexEnd < totalPages - 1)   pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="pagination-container mt-d">
      <ul className="pagination">
        <li
          className={`pagination-item pageArrow ${currentPage === 1 ? "disabled" : ""}`}
          onClick={handlePrevClick}
        >
          <span>
            <img src={Arrow} className="angleLeft" alt="Arrow" />
          </span>
        </li>

        {isMobile ? (
          (() => {
            const mobilePages = getMobileVisiblePages();
            const showEndEllipsis =
              mobilePages[mobilePages.length - 1] < totalPages;
            return (
              <>
                {mobilePages.map((page) => (
                  <li
                    key={page}
                    className={`pagination-item ${
                      currentPage === page ? "active" : ""
                    }`}
                    onClick={() => handlePageClick(page)}
                  >
                    {page}
                  </li>
                ))}
                {showEndEllipsis && (
                  <li className="pagination-item ellipsis">...</li>
                )}
              </>
            );
          })()
        ) : (
          visiblePages.map((page, index) =>
            page === "..." ? (
              <li
                key={`ellipsis-${index}`}
                className="pagination-item ellipsis"
              >
                ...
              </li>
            ) : (
              <li
                key={page}
                className={`pagination-item ${
                  currentPage === page ? "active" : ""
                }`}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </li>
            )
          )
        )}

        <li
          className={`pagination-item pageArrow ${
            currentPage === totalPages ? "disabled" : ""
          }`}
          onClick={handleNextClick}
        >
          <span>
            <img src={Arrow} className="angleRight" alt="Arrow" />
          </span>
        </li>
      </ul>
    </div>
  );
};

export default Pagination;
